import { DocumentServicesObject } from '@wix/document-services-types';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
  UnifiedComponentsInstallationError,
} from './errors';
import { chainPromisesAsync } from './utils';
import { getBIReporter } from './bi';
import { AddAppData, AppData } from '../../../../types/unifiedComponents';
import {
  InstallOptions,
  InstallCallbacks,
} from '@wix/editor-platform-host-integration-apis';
import { PlatformContext } from '../../../../types/platformApi';
import { installSingleUnifiedComponentsApp } from './installSingleApp';
import { ComponentType } from '@wix/ambassador-app-service-webapp/types';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { autoPopulatePlugins } from './pluginsAutoPopulate';

type AllAppsDataAfterInstallation = {
  nonUnifiedAppsData: AddAppData[];
  unifiedAppsData: AddAppData[];
};

function isUnifiedComponentsApp(appData: AppData) {
  const unifiedComponent = (appData?.components ?? []).filter(
    ({ data, type }) =>
      type !== ComponentType.WIDGET_PLUGIN && data && 'installation' in data,
  );
  return unifiedComponent.length > 0;
}

function installAppsInternals(
  context: PlatformContext,
  documentServices: DocumentServicesObject,
  appDefIds: string[],
  installOptions?: InstallOptions,
  callbacks?: InstallCallbacks,
): Promise<void> {
  const { appsOptions, ...otherOptions } = installOptions ?? {};
  const biReporter = getBIReporter(context, appDefIds, appsOptions);

  function triggerFinishInstallAllAppsCallback(
    provisionedApps: AllAppsDataAfterInstallation,
  ) {
    return new Promise<void>((resolve) => {
      callbacks?.finishAllCallback?.([
        ...provisionedApps.nonUnifiedAppsData,
        ...provisionedApps.unifiedAppsData,
      ]);
      resolve();
    });
  }

  function handlePluginsAutoPopulate(appData: AppData): Promise<void> {
    // Will remove this check once delete old logic in editors https://wix.atlassian.net/browse/EP-5329
    const appOptions = appsOptions?.[appData.appDefinitionId] || {};
    if (appOptions.pluginsAutoPopulate === undefined) {
      return Promise.resolve();
    }

    return autoPopulatePlugins(
      {
        ...context,
        documentServices,
      },
      appOptions,
      appData.appDefinitionId,
      appData.components,
    );
  }

  function installNonUnifiedApp(appData: AppData) {
    const callbackInvoke: Record<string, boolean> = {};
    const pluginsAutoPopulationPromises: Promise<void>[] = [];

    return new Promise<AddAppData>((resolve, reject) => {
      callbackInvoke[appData.appDefinitionId] = false;
      documentServices.platform.addApps([appData.appDefinitionId], {
        ...(appsOptions?.[appData.appDefinitionId]
          ? {
              [appData.appDefinitionId]: appsOptions[appData.appDefinitionId],
            }
          : {}),
        ...otherOptions,
        singleAppCallback: (appDefId: string, addedAppData: AddAppData) => {
          const singleAppHandler = () => {
            callbackInvoke[appDefId] = true;
            callbacks?.singleAppCallback?.(appDefId, addedAppData);
            resolve(addedAppData);
          };
          const pluginsAutoPopulatePromise =
            handlePluginsAutoPopulate(appData).then(singleAppHandler);

          pluginsAutoPopulationPromises.push(pluginsAutoPopulatePromise);
        },
        finishAllCallback: (addedAppData: AddAppData[]) => {
          const finishAllHandler = () => {
            if (pluginsAutoPopulationPromises.length > 0) {
              Promise.all(pluginsAutoPopulationPromises)
                .then(() => {
                  resolve(addedAppData[0]);
                })
                .catch(reject);
            }
            if (!callbackInvoke[appData.appDefinitionId]) {
              const resolvedData = addedAppData.find(
                (data) => appData.appDefinitionId === data.appDefinitionId,
              );
              if (resolvedData) {
                callbacks?.singleAppCallback?.(
                  appData.appDefinitionId,
                  resolvedData,
                );
                resolve(resolvedData);
              } else {
                const error = new Error('data not retrieve from document');
                reject(
                  createUnifiedComponentsInstallationError(
                    ErrorType.nonUnifiedInstallationFailed,
                    'Error occur in non-unified applications installation process',
                  )
                    .withAppDefIds(appData.appDefinitionId)
                    .withParentError(error as Error),
                );
              }
            }
          };
          Promise.allSettled(pluginsAutoPopulationPromises).then(() => {
            finishAllHandler();
          });
        },
        onError: (error: Error, _errName: string, appDefinitionId: string) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.nonUnifiedInstallationFailed,
              'Error occur in non-unified applications installation process',
            )
              .withAppDefIds(appDefinitionId)
              .withParentError(error as Error),
          ),
      });
    });
  }

  function installAllAppsByClassification(appsData: AppData[]) {
    const resultedAddAppsData: AllAppsDataAfterInstallation = {
      nonUnifiedAppsData: [],
      unifiedAppsData: [],
    };

    const updateAppDataAfterInstallation = (appData: AppData) => {
      if (appData.isUnifiedComponentsApp) {
        return installSingleUnifiedComponentsApp(
          { ...context, documentServices },
          appData,
          biReporter,
          appsOptions?.[appData.appDefinitionId],
          callbacks?.singleAppCallback,
        ).then((addedAppData) =>
          resultedAddAppsData.unifiedAppsData.push(addedAppData),
        );
      } else {
        return installNonUnifiedApp(appData).then((addedAppData) =>
          resultedAddAppsData.nonUnifiedAppsData.push(addedAppData),
        );
      }
    };

    return new Promise<AllAppsDataAfterInstallation>((resolve, reject) =>
      chainPromisesAsync(appsData, updateAppDataAfterInstallation)
        .then(() => resolve(resultedAddAppsData))
        .catch(reject),
    );
  }

  function validateIfNoAppProvisioned(provisionedApps: AppData[]) {
    return new Promise<AppData[]>((resolve, reject) => {
      if (!provisionedApps || provisionedApps.length === 0) {
        reject(
          createUnifiedComponentsInstallationError(
            ErrorType.noAppRecivedFromProvision,
            'None of the apps could be provisioned',
          ).withAppDefIds(appDefIds),
        );
      }
      resolve(provisionedApps);
    });
  }

  function classifyProvisionedApps(appsData: AppData[]) {
    return new Promise<AppData[]>((resolve) =>
      resolve(
        appsData.map((appData: AppData) => {
          return {
            ...appData,
            isUnifiedComponentsApp: isUnifiedComponentsApp(appData),
          };
        }),
      ),
    );
  }

  function provisionApps(): Promise<AppData[]> {
    return biReporter.withBI(
      'PROVISION_APPS',
      {},
      function provisionAndClassifyApps() {
        return new Promise<AppData[]>((resolve, reject) => {
          documentServices.platform.installationFlow
            .provisionApps(appDefIds, appsOptions ?? {})
            .then((appsData) => resolve(appsData as AppData[]))
            .catch((error: Error) =>
              reject(
                createUnifiedComponentsInstallationError(
                  ErrorType.provisionAppsFailed,
                  'Could not provision apps',
                )
                  .withAppDefIds(appDefIds)
                  .withParentError(error),
              ),
            );
        });
      },
    );
  }

  function installAllApps() {
    return biReporter.withBI(
      'PLATFORM_INSTALLATION',
      {},
      () =>
        new Promise<void>((resolve, reject) => {
          provisionApps()
            .then(classifyProvisionedApps)
            .then(validateIfNoAppProvisioned)
            .then(installAllAppsByClassification)
            .then(triggerFinishInstallAllAppsCallback)
            .then(resolve)
            .catch((error: UnifiedComponentsInstallationError) => {
              callbacks?.onError?.(
                error,
                error.name,
                error.appDefIds ?? appDefIds.toString(),
              );
              reject(error);
            });
        }),
    );
  }

  return installAllApps();
}

let installationLevel = 0;

const createSetAutosave =
  (context: PlatformContext, experimentsAPI: IExperimentsApiAdapter) =>
  (enabled: boolean) => {
    if (experimentsAPI.enabled('se_toggleAutosaveOnInstall')) {
      context.platformApiMethods.editor.setAutosave(undefined, '', enabled);
    }
  };

export async function installApps(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  appDefIds: string[],
  options?: InstallOptions,
  callbacks?: InstallCallbacks,
): Promise<void> {
  const setAutosave = createSetAutosave(context, experimentsAPI);

  let shouldEnableAutosave = false;
  if (installationLevel === 0) {
    shouldEnableAutosave = true;
    setAutosave(false);
  }

  installationLevel += 1;

  try {
    return await installAppsInternals(
      context,
      documentServicesAPI,
      appDefIds,
      options,
      callbacks,
    );
  } catch (err) {
    // removing explicit error re-throw breaks errors handling in tests
    throw err;
  } finally {
    installationLevel -= 1;

    if (
      shouldEnableAutosave &&
      experimentsAPI.enabled('se_toggleAutosaveOnInstall')
    ) {
      shouldEnableAutosave = false;
      setAutosave(true);

      if (!options?.noForcedSaveAfterInstallationCompleted) {
        context.platformApiMethods.editor.saveInBackground();
      }
    }
  }
}
